import React, { useEffect, useState } from "react";
import Header from "../../Components/Header/Header";
import Footer from "../../Components/Footer/Footer";
import { toast } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { APIUrl, APIUrlToken } from "../../Utils/Utils";
import "../AthleteProfileComponent/AthleteProfileComponent.css";

// icons (only for the left icon slot your CSS expects)
import {
  FaRegUserCircle,
  FaInstagram,
  FaFacebook,
  FaSchool,
  FaHashtag,
} from "react-icons/fa";
import { FaXTwitter, FaTiktok, FaYoutube } from "react-icons/fa6";
import { FaUserCircle } from "react-icons/fa";
import { FaCamera } from "react-icons/fa";

import { MdSportsSoccer, MdEmail } from "react-icons/md";
import { GiWeight } from "react-icons/gi";
import { MdClass } from "react-icons/md";

const SPORTS = [
  "Cross Country",
  "Football",
  "Field Hockey",
  "Soccer",
  "Volleyball",
  "Basketball",
  "Bowling",
  "Fencing",
  "Gymnastics",
  "Ice Hockey",
  "Indoor Track and Field",
  "Rifle",
  "Skiing",
  "Swimming and Diving",
  "Wrestling",
  "Baseball",
  "Beach Volleyball",
  "Golf",
  "Lacrosse",
  "Outdoor Track and Field",
  "Rowing",
  "Softball",
  "Tennis",
  "Water Polo",
];

const CLASSES = ["Freshman", "Sophomore", "Junior", "Senior"];

const AthleteProfileComponent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [previewSrc, setPreviewSrc] = useState(null);

  const [formData, setFormData] = useState({
    // radios
    sport_level: "",
    gender_division: "", // "Men" | "Women" (REQUIRED)

    // identity
    first_name: "", // REQUIRED
    last_name: "", // REQUIRED
    email: "",
    // sport/school
    sport_name: "", // REQUIRED
    school_name: "", // REQUIRED
    class_name: "", // REQUIRED
    position: "",
    jersey_number: "",

    // height split inputs (feet + inches) — REQUIRED overall
    height_feet: "",
    height_inches: "",

    // weight (lbs)
    weight: "",

    // socials
    instagram_url: "",
    facebook_url: "",
    x_url: "",
    youtube_url: "",
    tiktok_url: "",

    // image (REQUIRED)
    profile_picture_base64: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    // prevent negatives for weight/jersey_number
    if (
      (name === "weight" || name === "jersey_number") &&
      value !== "" &&
      Number(value) < 0
    ) {
      return;
    }

    // inches 0–11
    if (name === "height_inches") {
      if (value === "")
        return setFormData((p) => ({ ...p, height_inches: "" }));
      const v = Math.max(0, Math.min(11, Number(value)));
      return setFormData((p) => ({ ...p, height_inches: String(v) }));
    }

    // feet 0–8 (sane bound)
    if (name === "height_feet") {
      if (value === "") return setFormData((p) => ({ ...p, height_feet: "" }));
      const v = Math.max(0, Math.min(8, Number(value)));
      return setFormData((p) => ({ ...p, height_feet: String(v) }));
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // convert to data URL for Postgres BYTEA
  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file.");
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result; // data:image/...;base64,....
      setPreviewSrc(dataUrl);
      setFormData((prev) => ({
        ...prev,
        profile_picture_base64: dataUrl,
      }));
    };
    reader.onerror = () => toast.error("Failed to read image.");
    reader.readAsDataURL(file);
  };

  const heightDisplay = () => {
    const f = formData.height_feet;
    const i = formData.height_inches;
    if (f === "" || i === "") return null;
    return `${Number(f)}' ${Number(i)}"`;
  };

  const handleSubmit = async () => {
    // Required checks
    if (!formData.email?.trim()) {
      toast.error("Please enter Email.");
      return;
    }
    // 👇 Add your email validation check here
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error("Please enter a valid Email address.");
      return;
    }
    if (!formData.sport_level) return toast.error("Please select Sport Level.");
    if (!formData.gender_division)
      return toast.error("Please select Division.");
    if (!formData.first_name.trim())
      return toast.error("Please enter First Name.");
    if (!formData.last_name.trim())
      return toast.error("Please enter Last Name.");
    if (!formData.sport_name)
      return toast.error("Please select Name of Sport.");
    if (!formData.school_name.trim())
      return toast.error("Please enter Name of School.");
    if (!formData.class_name) return toast.error("Please select Class.");
    if (formData.height_feet === "" || formData.height_inches === "")
      return toast.error("Please enter Height (feet and inches).");
    if (!formData.profile_picture_base64)
      return toast.error("Please upload a Profile Photo.");
    // --- required checks ---
    if (
      !formData.first_name ||
      !formData.last_name ||
      !formData.sport_name ||
      !formData.school_name
    ) {
      toast.error("Please fill all required fields.");
      return;
    }

    const payload = {
      // identity
      first_name: formData.first_name || null,
      last_name: formData.last_name || null,
      email_address: formData.email,
      // sport/school
      sport_name: formData.sport_name || null,
      school_name: formData.school_name || null,
      class_name: formData.class_name || null,
      // radios
      sport_level: formData.sport_level,
      gender_division: formData.gender_division,
      position: formData.position || null,
      jersey_number:
        formData.jersey_number !== "" ? Number(formData.jersey_number) : null,

      // height as formatted text
      height: heightDisplay(), // e.g., 6' 3"  (Ensure DB column is TEXT)

      // weight (lbs)
      weight: formData.weight !== "" ? Number(formData.weight) : null,

      // socials
      instagram_url: formData.instagram_url || null,
      facebook_url: formData.facebook_url || null,
      x_url: formData.x_url || null,
      youtube_url: formData.youtube_url || null,
      tiktok_url: formData.tiktok_url || null,

      // image
      profile_picture_base64: formData.profile_picture_base64,
    };

    const url = `${APIUrl()}/v1/create_player_profiles`;
    const options = {
      method: "POST",
      headers: { "X-API-Key": APIUrlToken() },
      data: payload,
      url,
    };

    try {
      setLoading(true);
      await axios(options);
      toast.success("Athlete profile created successfully");

      // Reset
      setFormData({
        sport_level: "",
        gender_division: "",
        first_name: "",
        last_name: "",
        sport_name: "",
        school_name: "",
        class_name: "",
        position: "",
        jersey_number: "",
        height_feet: "",
        height_inches: "",
        weight: "",
        instagram_url: "",
        facebook_url: "",
        x_url: "",
        youtube_url: "",
        tiktok_url: "",
        profile_picture_base64: "",
      });
      setPreviewSrc(null);

      navigate("/");
    } catch (error) {
      toast.error("Error occurred while saving. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Helpers to keep alignment identical to Contact Us layout
  const fullWidth = { width: "100%", maxWidth: 720 };
  const row = {
    ...fullWidth,
    display: "flex",
    gap: 16,
    alignItems: "stretch",
    flexWrap: "wrap",
  };
  const col = { flex: 1, minWidth: 280 };

  return (
    <>
      <div className="page-main-container-aboutus">
        <div className="content-section-changepass">
          <h3>Athlete NIL Profile</h3>

          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {/* Radios Row: clearly separated groups in one row */}
            <div className="input-section-changepass cu-nil" style={row}>
              {/* Sport Level box */}
              <div className="input-group-changepass" style={col}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  Select Sport Level <span style={{ color: "red" }}>*</span>
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="sport_level"
                      value="College Sports"
                      checked={formData.sport_level === "College Sports"}
                      onChange={handleChange}
                    />
                    College Sports
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="sport_level"
                      value="High School Sports"
                      checked={formData.sport_level === "High School Sports"}
                      onChange={handleChange}
                    />
                    High School Sports
                  </label>
                </div>
              </div>

              {/* Division box */}
              <div className="input-group-changepass" style={col}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  Select Division <span style={{ color: "red" }}>*</span>
                </label>
                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="gender_division"
                      value="Men"
                      checked={formData.gender_division === "Men"}
                      onChange={handleChange}
                    />
                    Men
                  </label>
                  <label
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                    }}
                  >
                    <input
                      type="radio"
                      name="gender_division"
                      value="Women"
                      checked={formData.gender_division === "Women"}
                      onChange={handleChange}
                    />
                    Women
                  </label>
                </div>
              </div>
            </div>

            {/* First Name */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaRegUserCircle />
                </div>
                <input
                  type="text"
                  name="first_name"
                  placeholder="First Name *"
                  className="form-control-changepass-user"
                  value={formData.first_name}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Last Name */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaRegUserCircle />
                </div>
                <input
                  type="text"
                  name="last_name"
                  placeholder="Last Name *"
                  className="form-control-changepass-user"
                  value={formData.last_name}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>
            {/* Email (optional) */}
            <div
              className="input-section-changepass cu-nil"
              style={{ width: "100%", maxWidth: 720 }}
            >
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <MdEmail />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  className="form-control-changepass-user"
                  value={formData.email}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Name of Sport: SELECT (like Class) */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <MdSportsSoccer />
                </div>
                <select
                  name="sport_name"
                  className="form-control-changepass-user"
                  value={formData.sport_name}
                  onChange={handleChange}
                  style={{ background: "transparent" }}
                >
                  <option value="" disabled>
                    Select Name of Sport *
                  </option>
                  {SPORTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Name of School */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaSchool />
                </div>
                <input
                  type="text"
                  name="school_name"
                  placeholder="Name of School *"
                  className="form-control-changepass-user"
                  value={formData.school_name}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Class: dropdown */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <MdClass />
                </div>
                <select
                  name="class_name"
                  className="form-control-changepass-user"
                  value={formData.class_name}
                  onChange={handleChange}
                  style={{ background: "transparent" }}
                >
                  <option value="" disabled>
                    Select Class *
                  </option>
                  {CLASSES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row: Position | Jersey Number */}
            <div className="input-section-changepass cu-nil" style={row}>
              <div className="input-group-changepass" style={col}>
                <div className="input-icon-changepass">
                  <MdSportsSoccer />
                </div>
                <input
                  type="text"
                  name="position"
                  placeholder="Position (if applicable)"
                  className="form-control-changepass-user"
                  value={formData.position}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
              <div className="input-group-changepass" style={col}>
                <div className="input-icon-changepass">
                  <FaHashtag />
                </div>
                <input
                  type="number"
                  name="jersey_number"
                  placeholder="Jersey Number (if applicable)"
                  min={0}
                  className="form-control-changepass-user"
                  value={formData.jersey_number}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Height (single box w/ ft & in) | Weight (lbs) */}
            <div className="input-section-changepass cu-nil" style={row}>
              {/* Height combined */}
              <div className="input-group-changepass" style={col}>
                <label
                  style={{
                    display: "block",
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  Height (ft/in) <span style={{ color: "red" }}>*</span>
                </label>
                <div style={{ display: "flex", gap: 8, width: "100%" }}>
                  <input
                    type="number"
                    name="height_feet"
                    placeholder="ft"
                    min={0}
                    max={8}
                    className="form-control-changepass-user"
                    value={formData.height_feet}
                    onChange={handleChange}
                    autoComplete="off"
                    style={{ flex: 1 }}
                  />
                  <input
                    type="number"
                    name="height_inches"
                    placeholder="in"
                    min={0}
                    max={11}
                    className="form-control-changepass-user"
                    value={formData.height_inches}
                    onChange={handleChange}
                    autoComplete="off"
                    style={{ flex: 1 }}
                  />
                </div>
              </div>

              {/* Weight lbs */}
              <div className="input-group-changepass" style={col}>
                <div className="input-icon-changepass">
                  <GiWeight />
                </div>
                <input
                  type="number"
                  name="weight"
                  placeholder="Weight (lbs, if applicable)"
                  min={0}
                  className="form-control-changepass-user"
                  value={formData.weight}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Social links */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaInstagram />
                </div>
                <input
                  type="text"
                  name="instagram_url"
                  placeholder="Instagram Profile Link"
                  className="form-control-changepass-user"
                  value={formData.instagram_url}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaFacebook />
                </div>
                <input
                  type="text"
                  name="facebook_url"
                  placeholder="Facebook Profile Link"
                  className="form-control-changepass-user"
                  value={formData.facebook_url}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaXTwitter />
                </div>
                <input
                  type="text"
                  name="x_url"
                  placeholder="X Profile Link"
                  className="form-control-changepass-user"
                  value={formData.x_url}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaYoutube />
                </div>
                <input
                  type="text"
                  name="youtube_url"
                  placeholder="YouTube Profile Link"
                  className="form-control-changepass-user"
                  value={formData.youtube_url}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaTiktok />
                </div>
                <input
                  type="text"
                  name="tiktok_url"
                  placeholder="TikTok Profile Link"
                  className="form-control-changepass-user"
                  value={formData.tiktok_url}
                  onChange={handleChange}
                  autoComplete="off"
                />
              </div>
            </div>

            {/* Upload Photo (REQUIRED) */}
            <div className="input-section-changepass cu-nil" style={fullWidth}>
              <div className="input-group-changepass">
                <div className="input-icon-changepass">
                  <FaCamera />
                </div>

                {/* Hidden real file input */}
                <input
                  type="file"
                  accept="image/*"
                  id="profilePicInput"
                  style={{ display: "none" }}
                  onChange={handleImageChange}
                />

                {/* Custom label acts like the button */}
                <label
                  htmlFor="profilePicInput"
                  className="form-control-changepass-user"
                  style={{
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {previewSrc
                    ? "Change Profile Picture"
                    : "Upload Profile Picture"}
                </label>
              </div>

              {/* Preview thumbnail */}
              {previewSrc && (
                <div style={{ marginTop: 8 }}>
                  <img
                    src={previewSrc}
                    alt="Preview"
                    style={{ maxHeight: 120, borderRadius: 8 }}
                  />
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="button-section-changepass-user">
              <button onClick={handleSubmit} disabled={loading}>
                {loading ? "Submitting..." : "Submit Profile"}
              </button>
            </div>

            <p style={{ marginTop: 12, fontSize: 12, opacity: 0.8 }}>
              * If any non‑required fields are left blank, that row or section
              will not be displayed.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default AthleteProfileComponent;
